import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { useGeneralManagementState } from "../GeneralManagement/generalManagementState";
import "./survey2.scss";
import FieldsetContainer from "components/blocks/Delta/Inputs/Containers/FieldsetContainer";
import DOMPurify from "dompurify";
import {
  CategoriaPreguntaResponse,
  EstadoRespuesta,
  ObtenerEncuestaResponse,
  PreguntaResponse,
  RellenarEncuestaRequest,
  TipoPregunta,
  OpcionResponse,
  AmbitoEncuesta,
  GastarOportunidadRequest
} from "@solimat/solimat-web-endpoint/lib/survey-ingestion/dto";
import useRellenarEncuesta from "services/surveys/useRellenarEncuesta";
import TextAreaConContador from "./TextAreaConContador";
import useObtenerEncuestas from "services/surveys/useObtenerEncuestas";
import useGastarOportunidad from "services/surveys/useGastarOportunidad";
import useGetRole from "hooks/useGetRole";

type Guid = string;
type FormData = Record<string, string>;

interface SurveyModalProps {
  handleCloseSurvey: () => void;
}

const fieldNames = {
  main: (preguntaId: Guid) => `q-${preguntaId}`,
  tlForOption: (preguntaId: Guid, opcionId: Guid) => `q-${preguntaId}-opt-${opcionId}-tl`,
  tlForTextOnly: (preguntaId: Guid) => `q-${preguntaId}-tl`
};

const SurveyModal: React.FC<SurveyModalProps> = ({ handleCloseSurvey }) => {
  const { isImpersonation } = useGetRole;
  const { register, handleSubmit, watch, getValues, setValue, unregister, reset } = useForm<FormData>({ mode: "onChange" });
  const { state, dispatch } = useGeneralManagementState();
  const { encuestas, obtenerEncuestas } = useObtenerEncuestas();
  const { rellenarEncuesta, loadingRellenarEncuesta, errorRellenarEncuesta } = useRellenarEncuesta();
  const { gastarOportunidad, loadingGastarOportunidad, errorGastarOportunidad } = useGastarOportunidad();
  const [esIntroduccion, setEsIntroduccion] = useState(true);
  const [indiceCategoriaActual, setIndiceCategoriaActual] = useState(0);
  const [indicePreguntaActual, setIndicePreguntaActual] = useState(0);
  const [mostrarConfirmacionDeCierre, setMostrarConfirmacionDeCierre] = useState(false);
  const [mostrarAgradecimiento, setMostrarAgradecimiento] = useState(false);
  const [encuesta, setEncuesta] = useState<ObtenerEncuestaResponse | null>(null);
  const [hayPregObligatorias, setHayPregObligatorias] = useState<boolean>(false);
  const [hayPregOpcionales, setHayPregOpcionales] = useState<boolean>(false);
  const [segmentosNoRespondidos, setSegmentosNoRespondidos] = useState<number[]>([]);
  const [formActualizado, setFormActualizado] = useState<boolean>(false);
  const previousMainSelectionsRef = useRef<string[]>([]);
  const esSoloLectura = encuesta ? (encuesta.estadoRespuesta === EstadoRespuesta.Enviada || encuesta.caducada) : false;

  useEffect(() => {
    let encuestaTemp: ObtenerEncuestaResponse | null = null;
    
    if (!state.showSurvey.show) {
      setEncuesta(encuestaTemp);
      return;
    }
    
    if (state.showSurvey.id !== "") {
      encuestaTemp = state.surveys?.find((s: ObtenerEncuestaResponse) => s.asignacionId === state.showSurvey.id) ?? null;
    }
    else {
      encuestaTemp = state.surveys
        ?.filter((s: ObtenerEncuestaResponse) => s.ambito !== AmbitoEncuesta.Funcional
          && (s.estadoRespuesta === EstadoRespuesta.Vacia || s.estadoRespuesta === EstadoRespuesta.Rellena)
          && !s.caducada)
        ?.sort((a, b) => b.prioridad - a.prioridad)[0] ?? null;
    }
    
    if (encuestaTemp) {
      const defaults: FormData = {};

      encuestaTemp.categoriasPreguntas.forEach(categoriaPreguntas => {
        categoriaPreguntas.preguntas.forEach(pregunta => {
          if (pregunta.tipo === TipoPregunta.SeleccionUnica || pregunta.tipo === TipoPregunta.OpcionYTextoLibre) {
            const seleccionada = pregunta.opciones?.find((o: any) => o.respondida);

            if (seleccionada) {
              defaults[fieldNames.main(pregunta.preguntaId)] = seleccionada.opcionId;

              if (seleccionada.permiteTextoLibre && seleccionada.textoLibre && seleccionada.textoLibre.trim() !== "") {
                defaults[fieldNames.tlForOption(pregunta.preguntaId, seleccionada.opcionId)] = seleccionada.textoLibre;
              }
            }
          } else if (pregunta.tipo === TipoPregunta.TextoLibre) {
            const tl = pregunta.opciones?.[0]?.textoLibre;
            if (tl && tl.trim() !== "") {
              defaults[fieldNames.tlForTextOnly(pregunta.preguntaId)] = tl;
            }
          }
        });
      });
      reset(defaults);

      setEncuesta(encuestaTemp);
      setHayPregObligatorias(encuestaTemp.categoriasPreguntas?.some(c => c.preguntas.some(p => p.obligatoria)) ?? false);
      setHayPregOpcionales(encuestaTemp.categoriasPreguntas?.some(c => c.preguntas.some(p => !p.obligatoria)) ?? false);
    }
  }, [state.surveys, state.showSurvey.id, state.showSurvey.show]);

  useEffect(() => {
    if (state.showSurvey.show) {
      reset();
      setEsIntroduccion(true);
      setFormActualizado(false);
      setIndiceCategoriaActual(0);
      setIndicePreguntaActual(0);
      setMostrarConfirmacionDeCierre(false);
      setMostrarAgradecimiento(false);
      setSegmentosNoRespondidos([]);
      previousMainSelectionsRef.current = [];
    }
  }, [state.showSurvey.show]);

  useEffect(() => {
    setEsIntroduccion(true);
    setFormActualizado(false);
    setIndiceCategoriaActual(0);
    setIndicePreguntaActual(0);
    setSegmentosNoRespondidos([]);
    previousMainSelectionsRef.current = [];
  }, [state.showSurvey.id]);

  const preguntasIndizadas = useMemo(() => {
    if (!encuesta)
      return [];

    const list: Array<PreguntaResponse & { indiceCategoria: number; indicePregunta: number; categoriaTexto: string }> = [];
    encuesta.categoriasPreguntas.forEach((c, idxC) => {
      c.preguntas.forEach((p, idxP) =>
        list.push({ ...p, indiceCategoria: idxC, indicePregunta: idxP, categoriaTexto: c.texto })
      );
    });

    return list;
  }, [encuesta]);

  useEffect(() => {
    if (!encuesta)
      return;

    const noRespondidas: number[] = [];
    preguntasIndizadas.forEach((p, idx) => {
      if (p.obligatoria && !isPreguntaRespondida(p)) noRespondidas.push(idx);
    });
    if (noRespondidas.length > 0) {
      setIndicePreguntaActual(noRespondidas[0]);
      return;
    }
  }, [encuesta, preguntasIndizadas, state.showSurvey.show, state.showSurvey.id]);

  const indiceGlobalActual = useMemo(() => {
    if (!encuesta)
      return 0;

    const prevCount = encuesta.categoriasPreguntas
      .slice(0, indiceCategoriaActual)
      .reduce((acc, c) => acc + c.preguntas.length, 0);

    return prevCount + indicePreguntaActual;
  }, [encuesta, indiceCategoriaActual, indicePreguntaActual]);

  const categoriaActual: CategoriaPreguntaResponse | undefined =
    encuesta?.categoriasPreguntas[indiceCategoriaActual];

  const preguntaActual: PreguntaResponse | undefined =
    categoriaActual?.preguntas[indicePreguntaActual];

  const saltarAIndiceGlobal = useCallback((indiceGlobal: number) => {
    if (!encuesta)
      return;

    let remaining = indiceGlobal;
    for (let i = 0; i < encuesta.categoriasPreguntas.length; i++) {
      const len = encuesta.categoriasPreguntas[i].preguntas.length;
      if (remaining < len) {
        setIndiceCategoriaActual(i);
        setIndicePreguntaActual(remaining);
        break;
      }

      remaining -= len;
    }
  }, [encuesta]);

  const handleNext = useCallback(() => {
    if (!encuesta || !categoriaActual)
      return;

    if (indicePreguntaActual < categoriaActual.preguntas.length - 1) {
      setIndicePreguntaActual(prev => prev + 1);
    } else if (indiceCategoriaActual < encuesta.categoriasPreguntas.length - 1) {
      setIndiceCategoriaActual(prev => prev + 1);
      setIndicePreguntaActual(0);
    }
  }, [indicePreguntaActual, indiceCategoriaActual, encuesta, categoriaActual]);

  const handlePrev = useCallback(() => {
    if (!encuesta)
      return;

    if (indicePreguntaActual > 0) {
      setIndicePreguntaActual(prev => prev - 1);
    } else if (indiceCategoriaActual > 0) {
      const idxCatPrev = indiceCategoriaActual - 1;
      const catPrev = encuesta.categoriasPreguntas[idxCatPrev];
      setIndiceCategoriaActual(idxCatPrev);
      setIndicePreguntaActual(catPrev.preguntas.length - 1);
    }
  }, [indicePreguntaActual, indiceCategoriaActual, encuesta]);

  const formValues = watch();

  const isOptionSelected = (preguntaId: Guid, opcionId: Guid) => {
    const selected = formValues[fieldNames.main(preguntaId)];
    return selected === opcionId;
  };

  const mainFieldNames = useMemo(() => {
    if (!encuesta)
      return [] as string[];

    const names: string[] = [];
    encuesta.categoriasPreguntas.forEach(c => {
      c.preguntas.forEach(p => {
        if (p.tipo === TipoPregunta.SeleccionUnica || p.tipo === TipoPregunta.OpcionYTextoLibre) {
          names.push(fieldNames.main(p.preguntaId));
        }
      });
    });

    return names;
  }, [encuesta, fieldNames]);

  const mainSelections = watch(mainFieldNames);

  useEffect(() => {
    if (!encuesta)
      return;

    const currentSelections = mainSelections || [];
    const previousSelections = previousMainSelectionsRef.current;

    // Se compara con != en lugar de con !== para que acepte como iguales el null 
    // y el undefined y no identifique cambio en el formulario por esa diferencia
    const hasChanged = currentSelections.length !== previousSelections.length ||
      currentSelections.some((val, idx) => val != previousSelections[idx]);

    if (!hasChanged)
      return;
    if (!formActualizado && previousSelections.length > 0 && !esIntroduccion)
      setFormActualizado(true);

    previousMainSelectionsRef.current = [...currentSelections];

    let idx = 0;
    encuesta.categoriasPreguntas.forEach(c => {
      c.preguntas.forEach(p => {
        if (p.tipo !== TipoPregunta.SeleccionUnica && p.tipo !== TipoPregunta.OpcionYTextoLibre)
          return;

        const selectedOpcionId = mainSelections[idx];
        idx++;

        p.opciones?.forEach(o => {
          if (!o.permiteTextoLibre)
            return;

          const tlField = fieldNames.tlForOption(p.preguntaId, o.opcionId);
          const isSelected = selectedOpcionId === o.opcionId;

          if (!isSelected) {
            const currentValue = getValues(tlField);
            if (currentValue) {
              setValue(tlField, "");
            }
            unregister(tlField);
          }
        });
      });
    });
  }, [mainSelections, encuesta]);

  const isPreguntaRespondida = useCallback((pregunta: PreguntaResponse): boolean => {
    if (pregunta.tipo === TipoPregunta.SeleccionUnica) {
      const selected = getValues(fieldNames.main(pregunta.preguntaId));
      return !!selected;
    }

    if (pregunta.tipo === TipoPregunta.OpcionYTextoLibre) {
      const selected: string | undefined = getValues(fieldNames.main(pregunta.preguntaId));
      if (!selected)
        return false;

      const opcionSel = pregunta.opciones.find(o => o.opcionId === selected);
      if (!opcionSel)
        return false;

      if (opcionSel.permiteTextoLibre) {
        const tl = getValues(fieldNames.tlForOption(pregunta.preguntaId, opcionSel.opcionId));
        return !!(tl && tl.trim() !== "");
      }

      return true;
    }

    const tl = getValues(fieldNames.tlForTextOnly(pregunta.preguntaId));
    return !!(tl && tl.trim() !== "");
  }, [fieldNames]);

  useEffect(() => {
    if (!encuesta || segmentosNoRespondidos.length === 0)
      return;

    const updated = segmentosNoRespondidos.filter((idx) => {
      const pregunta = preguntasIndizadas[idx];
      if (!pregunta || !pregunta.obligatoria)
        return false;

      return !isPreguntaRespondida(pregunta);
    });

    if (updated.length !== segmentosNoRespondidos.length) {
      setSegmentosNoRespondidos(updated);
    }
  }, [formValues, encuesta, preguntasIndizadas, segmentosNoRespondidos]);

  const buildPayload = (encuesta: ObtenerEncuestaResponse, isPartial: boolean): RellenarEncuestaRequest => {
    const preguntasRellenas: any[] = [];

    encuesta.categoriasPreguntas.forEach(c => {
      c.preguntas.forEach(p => {
        if (p.tipo === TipoPregunta.SeleccionUnica || p.tipo === TipoPregunta.OpcionYTextoLibre) {
          const selectedOpcionId = getValues(fieldNames.main(p.preguntaId));
          if (selectedOpcionId) {
            const opcion = p.opciones.find(o => o.opcionId === selectedOpcionId);
            const pr: any = { preguntaId: p.preguntaId, opcionesRellenas: [] as any[] };

            if (opcion) {
              const maybeTL = opcion.permiteTextoLibre
                ? getValues(fieldNames.tlForOption(p.preguntaId, opcion.opcionId))
                : "";

              pr.opcionesRellenas.push({
                opcionId: opcion.opcionId,
                ...(opcion.permiteTextoLibre && maybeTL ? { textoLibre: maybeTL } : {})
              });

              preguntasRellenas.push(pr);
            }
          }
        } else if (p.tipo === TipoPregunta.TextoLibre) {
          const tl = getValues(fieldNames.tlForTextOnly(p.preguntaId));
          if (tl && tl.trim() !== "") {
            const opcionParaTexto: OpcionResponse | undefined = p.opciones?.[0];
            if (opcionParaTexto) {
              preguntasRellenas.push({
                preguntaId: p.preguntaId,
                opcionesRellenas: [{ opcionId: opcionParaTexto.opcionId, textoLibre: tl }]
              });
            }
          }
        }
      });
    });

    return {
      enviar: !isPartial,
      asignacionId: encuesta.asignacionId,
      encuestaId: encuesta.encuestaId,
      encuestaVersionId: encuesta.encuestaVersionId,
      expedienteId: encuesta.expedienteId,
      preguntasRellenas
    } as RellenarEncuestaRequest;
  };

  const onSubmit = useCallback(async (data: FormData, isPartial: boolean) => {
    if (!encuesta)
      return;

    if (!isPartial) {
      const noRespondidas: number[] = [];
      preguntasIndizadas.forEach((p, idx) => {
        if (p.obligatoria && !isPreguntaRespondida(p)) noRespondidas.push(idx);
      });
      if (noRespondidas.length > 0) {
        setSegmentosNoRespondidos(noRespondidas);

        // Opcional: saltar a la primera pendiente
        // if (noRespondidas.length)
        //   saltarAIndiceGlobal(noRespondidas[0]);

        return;
      }
    }

    const payload = buildPayload(encuesta, isPartial);
    const resultId = await rellenarEncuesta(payload);
    if (!resultId)
      return;

    const encuestasActualizadas = (state.surveys || []).map(
      (s: ObtenerEncuestaResponse) => s.asignacionId === encuesta?.asignacionId
        ? !isPartial
          ? { ...s, estadoRespuesta: EstadoRespuesta.Enviada, numeroOportunidades: s.numeroOportunidades + 1 }
          : { ...s, estadoRespuesta: EstadoRespuesta.Rellena, numeroOportunidades: s.numeroOportunidades + 1 }
        : s
    );
    dispatch({ type: "SET_SURVEYS", payload: encuestasActualizadas && encuestasActualizadas });

    if (!isPartial) {
      setMostrarAgradecimiento(true);
    }
    else {
      obtenerEncuestas();
      setMostrarConfirmacionDeCierre(false);
      handleCloseSurvey();
    }
  }, [encuesta, preguntasIndizadas]);

  useEffect(() => {
    dispatch({
      type: "SET_SURVEYS",
      payload: encuestas && encuestas
    });
  }, [encuestas]);

  const onInvalid = useCallback(() => {
    if (!encuesta) return;

    const noRespondidas: number[] = [];
    preguntasIndizadas.forEach((p, idx) => {
      if (p.obligatoria && !isPreguntaRespondida(p)) {
        noRespondidas.push(idx);
      }
    });

    setSegmentosNoRespondidos(noRespondidas);

    // Opcional: saltar a la primera pendiente
    // if (noRespondidas.length)
    //   saltarAIndiceGlobal(noRespondidas[0]);
  }, [encuesta, preguntasIndizadas]);

  const handleCloseEncuesta = useCallback(() => {
    if (esIntroduccion || !formActualizado || esSoloLectura) {
      if (!esSoloLectura) {
        void gastarOportunidad(
          {
            asignacionId: encuesta.asignacionId,
            encuestaVersionId: encuesta.encuestaVersionId
          } as GastarOportunidadRequest
        ).catch(() => {});
        const encuestasActualizadas = (state.surveys || []).map(
          (s: ObtenerEncuestaResponse) => s.asignacionId === encuesta?.asignacionId
            ? { ...s, numeroOportunidades: s.numeroOportunidades + 1 }
            : s
        );
        dispatch({ type: "SET_SURVEYS", payload: encuestasActualizadas && encuestasActualizadas });
      }

      handleCloseSurvey();
    }
    else {
      setMostrarConfirmacionDeCierre(true);
    }
  }, [esIntroduccion, formActualizado, encuesta]);

  const handleCloseConfirmacionDeCierre = useCallback(() => {
    void gastarOportunidad(
      {
        asignacionId: encuesta.asignacionId,
        encuestaVersionId: encuesta.encuestaVersionId
      } as GastarOportunidadRequest
    ).catch(() => {});
    const encuestasActualizadas = (state.surveys || []).map(
      (s: ObtenerEncuestaResponse) => s.asignacionId === encuesta?.asignacionId
        ? { ...s, numeroOportunidades: s.numeroOportunidades + 1 }
        : s
    );
    dispatch({ type: "SET_SURVEYS", payload: encuestasActualizadas && encuestasActualizadas });

    setMostrarConfirmacionDeCierre(false);
    handleCloseSurvey();
  }, [encuesta]);

  const handleCloseAgradecimiento = useCallback(() => {
    obtenerEncuestas();

    const hayActiva = state.surveys.filter(
      (s: ObtenerEncuestaResponse) => s.asignacionId !== encuesta?.asignacionId
    ).some((x: ObtenerEncuestaResponse) => (x.estadoRespuesta === EstadoRespuesta.Vacia || x.estadoRespuesta === EstadoRespuesta.Rellena) && !x.caducada);
    if (hayActiva) {
      setTimeout(() => {
        dispatch({ type: "SHOW_SURVEY", payload: { show: true, id: '' } });
      }, 2000);
    }

    setMostrarAgradecimiento(false);
    handleCloseSurvey();
  }, [state.surveys, encuesta]);

  const handleStartSurvey = useCallback(() => {
    setEsIntroduccion(false);
    saltarAIndiceGlobal(indicePreguntaActual);
  }, [indicePreguntaActual]);

  if (!encuesta)
    return null;

  const fechaCaducidadEfectivaFormateada = encuesta.fechaCaducidadEfectiva ?
    (encuesta.fechaCaducidadEfectiva instanceof Date
      ? encuesta.fechaCaducidadEfectiva
      : new Date(`${encuesta.fechaCaducidadEfectiva}Z`))
    : null;

  return (
    <>
      <Modal
        className="survey-modal"
        dialogClassName="survey-modal-dialog"
        contentClassName="survey-modal-content"
        show={state.showSurvey.show && !isImpersonation() && !mostrarConfirmacionDeCierre && !mostrarAgradecimiento}
        onHide={handleCloseEncuesta}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={encuesta.estadoRespuesta == EstadoRespuesta.Enviada || encuesta.caducada || ((!encuesta.limiteOportunidades || encuesta.limiteOportunidades > (encuesta.numeroOportunidades + 1)))}>
          <Modal.Title className="encuesta-header">
            <div>
              {esIntroduccion
                ? `${encuesta.titulo}`
                : `${encuesta.titulo} (${indiceGlobalActual + 1}/${preguntasIndizadas.length})`
              }
            </div>
            <div>
              {encuesta.expedienteId &&
                `EXP ${encuesta.expedienteId}`
              }
            </div>
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit((data) => onSubmit(data, false), onInvalid)}>
          <Modal.Body>
            {errorRellenarEncuesta && (
              <Alert variant="danger" className="mb-3">
                Ha ocurrido un error al enviar la encuesta. Inténtalo de nuevo.
              </Alert>
            )}

            {esIntroduccion ? (
              <>
                <div className="encuesta-introduccion" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(encuesta.texto) }} />
                <hr />
                <div className="text-center">
                  <Button onClick={handleStartSurvey} className="btn-box btn-accent">
                    {encuesta.estadoRespuesta === EstadoRespuesta.Vacia || encuesta.respuestasReiniciadas
                      ? "Empezar Encuesta"
                      : encuesta.estadoRespuesta === EstadoRespuesta.Rellena
                        ? "Continuar Encuesta"
                        : "Ver Encuesta"}
                  </Button>
                </div>
              </>
            ) : (
              preguntaActual && categoriaActual && (
                <>
                  <div className="category-header mb-3">
                    <h5 className="category">{categoriaActual.texto}</h5>
                  </div>

                  <FieldsetContainer
                    key={`question-${preguntaActual.preguntaId}`}
                    legend={preguntaActual.texto}
                    classC="question-fieldSet"
                  >
                    {(preguntaActual.tipo === TipoPregunta.SeleccionUnica ||
                      preguntaActual.tipo === TipoPregunta.OpcionYTextoLibre) && (
                        <>
                          <div className="radio-group-horizontal">
                            {preguntaActual.opciones?.map((o, idx) => {
                              const commonProps = {
                                type: "radio" as const,
                                label: o.texto,
                                id: `radio-${preguntaActual.preguntaId}-${o.opcionId}`,
                                name: fieldNames.main(preguntaActual.preguntaId),
                                inline: true
                              };

                              if (esSoloLectura) {
                                return (
                                  <div key={idx}>
                                    <Form.Check
                                      {...commonProps}
                                      checked={o.respondida}
                                      disabled
                                      readOnly
                                    />
                                  </div>
                                );
                              }
                              
                              return (
                                <div key={idx}>
                                  <Form.Check
                                    {...commonProps}
                                    value={o.opcionId}
                                    {...register(fieldNames.main(preguntaActual.preguntaId), {
                                      required: preguntaActual.obligatoria ? "Este campo es obligatorio" : false,
                                    })}
                                    defaultChecked={o.respondida}
                                  />
                                </div>
                              );
                            })}
                          </div>

                          {preguntaActual.opciones?.map((o, idx) => {
                            const selectedThis = isOptionSelected(preguntaActual.preguntaId, o.opcionId);
                            if (!o.permiteTextoLibre || !selectedThis)
                              return null;

                            return (
                              <div key={idx}>
                                <TextAreaConContador
                                  formControlKey={`tl-${preguntaActual.preguntaId}-${o.opcionId}`}
                                  required={preguntaActual.obligatoria}
                                  maxLength={2000}
                                  rows={2}
                                  id={fieldNames.tlForOption(preguntaActual.preguntaId, o.opcionId)}
                                  register={register}
                                  defaultValue={o.respondida ? o.textoLibre : undefined}
                                  disabled={esSoloLectura}
                                />
                              </div>
                            );
                          })}
                        </>
                      )}

                    {preguntaActual.tipo === TipoPregunta.TextoLibre && (
                      <TextAreaConContador
                        required={preguntaActual.obligatoria}
                        maxLength={2000}
                        rows={2}
                        id={fieldNames.tlForTextOnly(preguntaActual.preguntaId)}
                        register={register}
                        defaultValue={preguntaActual.opciones?.[0]?.textoLibre}
                        disabled={esSoloLectura}
                      />
                    )}

                    {preguntaActual.textoAdicional?.length &&
                      <div className="question-additional-text">{preguntaActual.textoAdicional}</div>
                    }
                  </FieldsetContainer>
                </>
              )
            )}
          </Modal.Body>

          {!esIntroduccion && (
            <div className="progress-container">
              <div className="progress-legend" aria-label="Leyenda de colores">
                {hayPregObligatorias &&
                  <span>
                    <i className="badge required" aria-hidden="true"></i>
                    Obligatoria
                  </span>
                }
                {hayPregOpcionales &&
                  <span>
                    <i className="badge optional" aria-hidden="true"></i>
                    Opcional
                  </span>
                }
              </div>

              <div className="progress-segments">
                {preguntasIndizadas.map((p, idx) => {
                  const isActive = indiceGlobalActual === idx;
                  const kindClass = p.obligatoria ? "required" : "optional";
                  const isUnanswered = segmentosNoRespondidos.includes(idx);
                  const progressAriaLabel = `Ir a la pregunta ${idx + 1}. ${p.obligatoria ? 'Obligatoria' : 'Opcional'}${isUnanswered ? '. Falta por responder' : ''}`;
                  return (
                    <div className="index-container" key={idx}>
                      <button
                        type="button"
                        onClick={() => saltarAIndiceGlobal(idx)}
                        className={`progress-segment ${isActive ? "active" : ""} ${kindClass}`}
                        title={`Pregunta ${idx + 1} · ${p.obligatoria ? "Obligatoria" : "Opcional"}${isUnanswered ? " · Falta por responder" : ""}`}
                        aria-label={progressAriaLabel}
                        aria-current={isActive ? 'step' : undefined}
                      >
                        {idx + 1}
                      </button>
                      {isUnanswered &&
                        <span className="unanswered-exclamation">!</span>
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Modal.Footer>
            {!esIntroduccion && (
              <>
                <div className="d-flex align-items-center">
                  <Button
                    onClick={handlePrev}
                    disabled={indiceGlobalActual === 0 || loadingRellenarEncuesta}
                    className="btn-box btn-accent me-3 btn-survey-anterior"
                  >
                    Anterior
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={indiceGlobalActual >= (preguntasIndizadas.length - 1) || loadingRellenarEncuesta}
                    className="btn-box btn-accent me-3 btn-survey-siguiente"
                  >
                    Siguiente
                  </Button>
                </div>
                {segmentosNoRespondidos.length > 0 &&
                  <div className="required-questiones-unanswered">
                    Hay preguntas obligatorias sin responder
                  </div>
                }
                {!esSoloLectura &&
                  <Button
                    type="submit"
                    className="btn-box btn-accent me-3 btn-survey-enviar"
                    disabled={loadingRellenarEncuesta}
                  >
                    {loadingRellenarEncuesta ? "Enviando…" : "Enviar"}
                  </Button>
                }
              </>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

      {mostrarConfirmacionDeCierre && (
        <Modal
          className="survey-modal"
          dialogClassName="survey-modal-dialog"
          contentClassName="survey-modal-content"
          show={mostrarConfirmacionDeCierre}
          onHide={handleCloseConfirmacionDeCierre}
          centered
        >
          <Modal.Header>
            <Modal.Title>¿Quieres guardar tus respuestas para terminar la encuesta más adelante?</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <div className="d-flex align-items-center save-survey-div">
              <Button
                onClick={handleCloseConfirmacionDeCierre}
                className="btn-box btn-accent me-3"
                disabled={loadingRellenarEncuesta}
              >
                Descartar cambios
              </Button>
              <Button
                onClick={handleSubmit((data) => onSubmit(data, true))}
                className="btn-box btn-accent me-3"
                disabled={loadingRellenarEncuesta}
              >
                {loadingRellenarEncuesta ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            {fechaCaducidadEfectivaFormateada &&
              <small className="text-muted">
                Recuerda que tienes hasta el {new Date(fechaCaducidadEfectivaFormateada).toLocaleString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })} a las {new Date(fechaCaducidadEfectivaFormateada).toLocaleString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })} para terminar y enviar esta encuesta
              </small>
            }
            <small className="text-muted">
              Podrás finalizar la encuesta desde el menú flotante de información
            </small>
          </Modal.Footer>
        </Modal>
      )}

      {mostrarAgradecimiento && (
        <Modal
          className="survey-modal"
          dialogClassName="survey-modal-dialog"
          contentClassName="survey-modal-content"
          show={mostrarAgradecimiento}
          onHide={handleCloseAgradecimiento}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>¡Gracias!</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <p className="mt-3">
              Gracias por tu tiempo. Tus comentarios nos ayudan a mejorar.
            </p>
            <p className="mt-3">
              ¡Que tengas un buen día!
            </p>
          </Modal.Body>
          <Modal.Footer />
        </Modal>
      )}
    </>
  );
};

export default SurveyModal;